import { Chip, Tooltip } from "@mui/material";

import { ProductStockQuantityField } from "../../../features/data/data-types";
import { CategoryRef, Product } from "../../../features/products/product";

/**
 * The "In Stock" chip, plus the one fact the chip could never show before: whether the
 * product-level total and the variant quantities actually agree.
 *
 * A product is in stock when the product-level "Stock Quantity" field is above 0 OR any
 * variant is above 0 - so the two can disagree, and when they do it is the product-level
 * number that every stock filter reads. Staying silent about that is what made a stale
 * import total look like a broken showroom filter.
 *
 * In sync (or no variants): "In Stock: 10"
 * Out of sync:              "In Stock P:9 V:10"
 */
export const StockChip = ({
  product,
  category,
}: {
  product: Product;
  category: CategoryRef;
}) => {
  const variants = product.variants ?? [];

  // A product's own field entries carry fieldId, not name - the name lives on the category
  // field. Resolve through the category exactly as getProductFields does; matching on
  // `name` against product.fields silently finds nothing and reads as "no stock".
  const stockField = category.group.fields.find(
    (f) => f.name === ProductStockQuantityField
  );
  const quantityField = stockField
    ? product.fields?.find((f) => f.fieldId === stockField.fieldId)
    : undefined;

  const hasProductLevel = !!quantityField?.value;
  const productLevel = parseInt(quantityField?.value || "0") || 0;

  if (variants.length === 0) {
    return (
      <Tooltip title="Stock Quantity for this product. Edit it on the Product Fields tab or on the Inventory page.">
        <Chip
          label={hasProductLevel ? `In Stock: ${productLevel}` : "In Stock"}
          size="small"
        />
      </Tooltip>
    );
  }

  const variantTotal = variants.reduce((sum, v) => sum + (v.quantity || 0), 0);
  // Only a product that carries its own total can disagree with its variants. Without one,
  // the variants are the only number there is and there is nothing to reconcile.
  const outOfSync = hasProductLevel && productLevel !== variantTotal;

  return (
    <Tooltip
      title={
        outOfSync
          ? `Product total is ${productLevel} but the ${variants.length} variants add up to ${variantTotal}. A product is in stock when EITHER number is above 0, so it takes both to hide it. The Stock Quantity field on the Product Fields tab can push one number onto the other.`
          : `Total across ${variants.length} variants. Change it on the Variants tab or the Inventory page.`
      }
    >
      <Chip
        label={
          outOfSync
            ? `In Stock P:${productLevel} V:${variantTotal}`
            : `In Stock: ${variantTotal}`
        }
        size="small"
        color={outOfSync ? "warning" : "default"}
      />
    </Tooltip>
  );
};
